import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { FetcherResult } from "../api-client/fetcher";
import { ModifiedSWRResponse, useSwrGet } from "../api-client/useSwrGet";
import {
  productTypeAccessory,
  productTypeCombo,
  productTypePackage,
  productTypeTire,
  productTypeWheel,
} from "../constants/productTypes";
import { useDealerId } from "../stores/hooks/useDealerId";
import { useNationalAccountId } from "../stores/hooks/useNationalAccountId";
import { useSearchForm } from "../stores/hooks/useSearchForm";
import { ComboBoxOption } from "../ui/ComboBox/useComboBox";
import { useGetVehicleModels } from "./useGetVehicleModels";
import { useGetVehicleSubmodels } from "./useGetVehicleSubmodels";

type Option = ComboBoxOption<string>;

export const useGetVehiclePartTypes = (): ModifiedSWRResponse<
  FetcherResult<Payload>,
  Option[]
> & {
  product?: string;
  vehicleManufacturerId?: number;
  vehicleModelId?: string;
  vehicleYearId?: number;
} => {
  const { vehicleManufacturerId, vehicleModelId, vehicleYearId } =
    useGetVehicleModels();
  const { t } = useTranslation();
  const [{ productType, vehicleModelClassId, vehicleSubmodel }] =
    useSearchForm();
  const dealerId = useDealerId();
  const nationalAccountId = useNationalAccountId();
  const { selectedSubmodel } = useGetVehicleSubmodels();

  const { data, ...res } = useSwrGet<Payload>(
    vehicleSubmodel && vehicleYearId
      ? "/retailSiteWebService/vehicle/getVehiclePartTypes.php"
      : null,
    {
      dealerId,
      nationalAccountId,
      vehicleCategoryId: vehicleModelClassId || "",
      vehicleManufacturerId: String(vehicleManufacturerId || ""),
      vehicleModelId: vehicleModelId || "",
      vehicleSubmodelId: selectedSubmodel?.value || "",
      vehicleYearId: String(vehicleYearId),
    }
  );

  const { has_ACC, has_PKG, has_TRE, has_WHL, has_WHL_TRE_COMBO } = data || {};

  const options = useMemo(() => {
    if (!data) return undefined;

    return [
      has_TRE && {
        label: t(`dealerProducts.${productTypeTire}`),
        value: productTypeTire,
      },
      has_WHL && {
        label: t(`dealerProducts.${productTypeWheel}`),
        value: productTypeWheel,
      },
      has_WHL_TRE_COMBO && {
        label: t(`dealerProducts.${productTypeCombo}`),
        value: productTypeCombo,
      },
      has_ACC && {
        label: t(`dealerProducts.${productTypeAccessory}`),
        value: productTypeAccessory,
      },
      has_PKG && {
        label: t(`dealerProducts.${productTypePackage}`),
        value: productTypePackage,
      },
    ].filter(Boolean) as Option[];
  }, [has_ACC, has_PKG, has_TRE, has_WHL, has_WHL_TRE_COMBO]);

  const product = options?.find(({ value }) => value === productType)?.label;

  return {
    ...res,
    data: options,
    product,
    vehicleManufacturerId,
    vehicleModelId,
    vehicleYearId,
  };
};

type Payload = {
  has_ACC: number;
  has_PKG: number;
  has_TRE: number;
  has_WHL: number;
  has_WHL_TRE_COMBO: number;
};
